import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';

export type DocType =
  | 'invoice'
  | 'receipt'
  | 'quote'
  | 'purchase_order'
  | 'delivery_note'
  | 'inspection'
  | 'contract'
  | 'other';

export type MatchStatus = 'no_po' | 'matched' | 'partial' | 'mismatch';

export interface ApInvoice {
  id: string;
  workspace_id: string;
  entity_id: string | null;
  vendor_id: string | null;
  status: 'pending_review' | 'approved' | 'scheduled' | 'paid' | 'disputed' | 'void';
  doc_type: DocType;
  po_id: string | null;
  match_status: MatchStatus;
  match_detail: Record<string, unknown>;
  source: string;
  invoice_number: string | null;
  issue_date: string | null;
  due_date: string | null;
  currency: string;
  subtotal_cents: string;
  tax_cents: string;
  total_cents: string;
  jp_qualified: boolean | null;
  jp_invoice_registration: string | null;
  tax_breakdown: Array<{ rate: number; taxableCents: number; taxCents: number }>;
  expense_account_code: string | null;
  posting_review_reason: string | null;
  ocr_confidence: string | null;
  file_url: string | null;
  raw_ocr_text: string | null;
  extracted: Record<string, unknown>;
  gl_postings: unknown[];
  ai_suggested: Record<string, unknown>;
  owner_user_id: string | null;
  notes: string | null;
  paid_at: string | null;
  created_at: string;
  updated_at: string;
}

export function useApInvoices(wsId: string | undefined, filters: Record<string, string | undefined> = {}) {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([k, v]) => v && params.append(k, v));
  return useQuery<ApInvoice[]>({
    queryKey: ['ap-invoices', wsId, filters],
    enabled: !!wsId,
    queryFn: async () => {
      const r = await api.get<ApInvoice[]>(`/workspaces/${wsId}/ap-invoices?${params.toString()}`);
      return r.data;
    },
  });
}

export function useInboxCounts(wsId: string | undefined) {
  return useQuery<{ pendingReview: number; dueSoon: number; pastDue: number; disputed: number }>({
    queryKey: ['inbox-counts', wsId],
    enabled: !!wsId,
    queryFn: async () => {
      const r = await api.get(`/workspaces/${wsId}/ap-invoices/inbox/counts`);
      return r.data;
    },
    refetchInterval: 30_000,
  });
}

export function useApInvoice(wsId: string | undefined, id: string | null | undefined) {
  return useQuery<ApInvoice>({
    queryKey: ['ap-invoice', wsId, id],
    enabled: !!wsId && !!id,
    queryFn: async () => {
      const r = await api.get<ApInvoice>(`/workspaces/${wsId}/ap-invoices/${id}`);
      return r.data;
    },
  });
}

/** Streams the original blob and opens it in a new tab. Uses fetch
 *  through the same /api proxy so the session cookie rides along. */
export async function openApInvoiceFile(wsId: string, id: string): Promise<void> {
  const r = await api.get(`/workspaces/${wsId}/ap-invoices/${id}/file`, {
    responseType: 'blob',
  });
  const url = URL.createObjectURL(r.data as Blob);
  window.open(url, '_blank', 'noopener,noreferrer');
  // Revoke after the new tab has had a chance to load. blob: URLs
  // share document origin so revoking too early breaks navigation.
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

export interface ApInvoicePatch {
  invoiceNumber?: string | null;
  issueDate?: string | null;
  dueDate?: string | null;
  currency?: string;
  subtotalCents?: number;
  taxCents?: number;
  totalCents?: number;
  vendorId?: string | null;
  notes?: string | null;
  expenseAccountCode?: string | null;
}

export function useUpdateApInvoice(wsId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, patch }: { id: string; patch: ApInvoicePatch }) => {
      const r = await api.patch(`/workspaces/${wsId}/ap-invoices/${id}`, patch);
      return r.data;
    },
    onSuccess: (_, { id }) => {
      qc.invalidateQueries({ queryKey: ['ap-invoice', wsId, id] });
      qc.invalidateQueries({ queryKey: ['ap-invoices', wsId] });
      qc.invalidateQueries({ queryKey: ['inbox-counts', wsId] });
    },
  });
}

/**
 * Apply the stored AI account suggestion via the receipted endpoint (records
 * an undoable ai_action). Returns the updated invoice + the action so the
 * caller can show an "Undo" receipt toast.
 */
export function useApplySuggestion(wsId: string | undefined) {
  const qc = useQueryClient();
  return useMutation<
    { invoice: ApInvoice; action: { id: string; after: Record<string, unknown> } },
    unknown,
    string
  >({
    mutationFn: async (id: string) =>
      (await api.post(`/workspaces/${wsId}/ap-invoices/${id}/apply-suggestion`)).data,
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: ['ap-invoice', wsId, id] });
      qc.invalidateQueries({ queryKey: ['ap-invoices', wsId] });
      qc.invalidateQueries({ queryKey: ['inbox-counts', wsId] });
      qc.invalidateQueries({ queryKey: ['ai-actions', wsId] });
    },
  });
}

export function useApproveApInvoice(wsId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const r = await api.post(`/workspaces/${wsId}/ap-invoices/${id}/approve`);
      return r.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ap-invoices', wsId] });
      qc.invalidateQueries({ queryKey: ['inbox-counts', wsId] });
    },
  });
}

/** Transition an AP invoice to disputed or void. */
export function useApInvoiceAction(wsId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, action }: { id: string; action: 'dispute' | 'void' }) =>
      (await api.post(`/workspaces/${wsId}/ap-invoices/${id}/${action}`)).data,
    onSuccess: (_, { id }) => {
      qc.invalidateQueries({ queryKey: ['ap-invoices', wsId] });
      qc.invalidateQueries({ queryKey: ['ap-invoice', wsId, id] });
      qc.invalidateQueries({ queryKey: ['inbox-counts', wsId] });
    },
  });
}

export interface ApInvoiceCreateManual {
  invoiceNumber?: string;
  issueDate?: string;
  dueDate?: string;
  currency?: string;
  subtotalCents?: number;
  taxCents?: number;
  totalCents?: number;
  jpInvoiceRegistration?: string;
  vendorName?: string;
  notes?: string;
  lineItems?: Array<{
    description: string;
    qty?: number;
    unitPriceCents?: number;
    taxRate?: number;
    amountCents?: number;
  }>;
}

export function useCreateApInvoice(wsId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: ApInvoiceCreateManual) => {
      const r = await api.post<ApInvoice>(`/workspaces/${wsId}/ap-invoices`, body);
      return r.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ap-invoices', wsId] });
      qc.invalidateQueries({ queryKey: ['inbox-counts', wsId] });
    },
  });
}

export interface BulkUploadResult {
  created: Array<{ apInvoiceId: string; filename: string }>;
  failed: Array<{ filename: string; error: string }>;
  count: number;
}

/** Bulk multi-file ingest. Optionally splits multi-page PDFs into one
 *  document per page on the server. */
export function useBulkUploadAp(wsId: string | undefined) {
  const qc = useQueryClient();
  return useMutation<BulkUploadResult, unknown, { files: File[]; splitPdfPages: boolean }>({
    mutationFn: async ({ files, splitPdfPages }) => {
      const form = new FormData();
      files.forEach((f) => form.append('files', f));
      form.append('splitPdfPages', splitPdfPages ? 'true' : 'false');
      const r = await api.post<BulkUploadResult>(
        `/workspaces/${wsId}/ap-invoices/upload/bulk`,
        form,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      return r.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ap-invoices', wsId] });
      qc.invalidateQueries({ queryKey: ['inbox-counts', wsId] });
    },
  });
}

export function useUploadApInvoice(wsId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (file: File) => {
      const form = new FormData();
      form.append('file', file);
      const r = await api.post(`/workspaces/${wsId}/ap-invoices/upload`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return r.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ap-invoices', wsId] });
      qc.invalidateQueries({ queryKey: ['inbox-counts', wsId] });
    },
  });
}
